import { AppointmentStatus } from '../appointments/enums/appointment-status.enum.js';
import { TreatmentLocation } from '../calls/enums/treatment-location.enum.js';
import { TreatmentPlanStatus } from '../treatment-plan/enums/treatment-plan-status.enum.js';
import { TreatmentUnitStatus } from '../treatment-plan/enums/treatment-unit-status.enum.js';
import { deriveKombiGroups } from './kombi-mirror.js';
/**
 * Folgeverordnung continuation (PRD #1343, RS-011). When an ACTIVE plan is
 * exhausted (no unbooked units left) or about to expire, its card offers
 * "Folgeverordnung scannen" instead of dead-ending: the patient scans a NEW
 * Verordnung (or types it) and books ONE pending hold anchored to the existing
 * episode. The anchor is this continuity signal — the established therapist and
 * the Kombi grouping pattern the episode already had — resolved purely from the
 * predecessor plan and its confirmed appointment history, never from patient
 * input. At the practice's confirm a NEW plan is created (a Folgeverordnung is a
 * new Verordnung, never an extension); the predecessor is untouched.
 */
/** Why an ACTIVE plan surfaces a Folgeverordnung card. */
export var FolgeverordnungReason;
(function (FolgeverordnungReason) {
    /** Every unit is booked — there is nothing left to continue against this plan. */
    FolgeverordnungReason["EXHAUSTED"] = "EXHAUSTED";
    /** The prescription is still valid but expires within the lookahead window. */
    FolgeverordnungReason["EXPIRING"] = "EXPIRING";
})(FolgeverordnungReason || (FolgeverordnungReason = {}));
/** How many days ahead of `validUntil` a still-valid plan starts offering a Folgeverordnung. */
export const FOLGEVERORDNUNG_EXPIRY_WINDOW_DAYS = 14;
/**
 * Resolve a predecessor episode's continuity signal (PRD #1343, RS-011) — the
 * established therapist and the Kombi grouping pattern — from the plan and the
 * appointments its units link to. Independent of remaining allowance or expiry,
 * so it serves both the Folgeverordnung slot search and the hold creation. No
 * I/O, no clock.
 *
 * Returns null when there is no confirmed history to anchor to (no established
 * therapist / no grouping to mirror) — a Folgeverordnung needs a real episode to
 * continue.
 */
export function resolveEpisodeContinuity(plan, linkedAppointments) {
    // Established therapist: the most recent non-cancelled linked appointment.
    const priorForPlan = linkedAppointments
        .filter((a) => a.status !== AppointmentStatus.CANCELLED && a.therapistId != null)
        .sort((a, b) => startMs(b) - startMs(a));
    const continuity = priorForPlan[0];
    if (!continuity || continuity.therapistId == null)
        return null;
    // The grouping pattern of the plan's last confirmed session (RS-006).
    const groups = deriveKombiGroups(plan, linkedAppointments);
    const group = groups[0] ?? [];
    if (group.length === 0)
        return null;
    return {
        predecessorPlanId: plan.id,
        officeLocationId: plan.officeLocationId,
        treatmentLabel: distinctHeilmittelLabel(plan),
        groupTreatmentConceptIds: group,
        therapistId: continuity.therapistId,
        therapistName: therapistDisplayName(continuity),
        therapistDurationMin: continuity.plannedDuration,
        treatmentLocation: continuity.treatmentLocation ?? TreatmentLocation.IN_PRESENCE,
    };
}
/**
 * Decide whether an ACTIVE plan should offer a Folgeverordnung card (PRD #1343,
 * RS-011) and, if so, resolve the continuity to anchor it to. A plan qualifies
 * when it has confirmed history (continuity) AND is either exhausted (no
 * unbooked units left) or expiring within {@link FOLGEVERORDNUNG_EXPIRY_WINDOW_DAYS}
 * of `now`. A plan with remaining units that is not near expiry is still a
 * normal follow-up target and is not surfaced here.
 *
 * @param opts.now effective "now" — the clock is passed in so the function stays pure
 * @param opts.expiryWindowDays override the default expiry lookahead (days)
 */
export function resolveFolgeverordnungCandidate(plan, linkedAppointments, opts) {
    if (plan.status !== TreatmentPlanStatus.ACTIVE)
        return { candidate: false };
    const continuity = resolveEpisodeContinuity(plan, linkedAppointments);
    if (!continuity)
        return { candidate: false };
    const exhausted = remainingSessions(plan, continuity.groupTreatmentConceptIds) === 0;
    if (exhausted) {
        return { candidate: true, continuity, reason: FolgeverordnungReason.EXHAUSTED };
    }
    const windowDays = opts.expiryWindowDays ?? FOLGEVERORDNUNG_EXPIRY_WINDOW_DAYS;
    if (isExpiringWithin(plan.validUntil, opts.now, windowDays)) {
        return { candidate: true, continuity, reason: FolgeverordnungReason.EXPIRING };
    }
    return { candidate: false };
}
/**
 * Complete bookable sessions left in the Kombi group — the scarcest member's
 * unbooked-unit count (a session needs one of each). Zero when the group is
 * exhausted.
 */
function remainingSessions(plan, groupConceptIds) {
    if (groupConceptIds.length === 0)
        return 0;
    const perConcept = groupConceptIds.map((conceptId) => plan.treatmentUnits.filter((u) => u.treatment?.id === conceptId &&
        u.status === TreatmentUnitStatus.PLANNED &&
        u.appointmentId === null).length);
    return Math.min(...perConcept);
}
/** True when a still-valid prescription's `validUntil` falls within the lookahead window. */
function isExpiringWithin(validUntil, now, windowDays) {
    if (validUntil == null)
        return false;
    const until = validUntil instanceof Date ? validUntil.getTime() : new Date(validUntil).getTime();
    const cutoff = now.getTime() + windowDays * 24 * 60 * 60 * 1000;
    return until <= cutoff;
}
/** The plan's distinct Heilmittel names joined for a card label. */
function distinctHeilmittelLabel(plan) {
    const names = [];
    const seen = new Set();
    for (const unit of plan.treatmentUnits) {
        const concept = unit.treatment;
        if (!concept || seen.has(concept.id))
            continue;
        seen.add(concept.id);
        names.push(concept.name);
    }
    return names.join(' + ');
}
function startMs(a) {
    return a.scheduledStart instanceof Date
        ? a.scheduledStart.getTime()
        : new Date(a.scheduledStart).getTime();
}
function therapistDisplayName(a) {
    const first = a.therapist?.firstName ?? '';
    const last = a.therapist?.lastName ?? '';
    return `${first} ${last}`.trim();
}
//# sourceMappingURL=folgeverordnung-context.js.map